import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";
import DateRangePicker, { useDefaultRange, previousPeriod } from "../components/DateRangePicker.jsx";

function formatAmount(n) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatQuantity(n) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function rangeLabel(range) {
  return `${range.startDate} – ${range.endDate}`;
}

// Renders "+$12.34 (+18%)" / "-$5.00 (-40%)" / "new" / "removed" / "—",
// colored red for an increase and green for a decrease (this is a cost
// dashboard — up is bad, down is good).
function DeltaCell({ a, b, formatFn }) {
  const diff = b - a;
  const roundedA = Math.round(a * 1000);
  const roundedB = Math.round(b * 1000);
  if (roundedA === 0 && roundedB === 0) return <span className="delta delta-flat">—</span>;
  if (roundedA === 0) return <span className="delta delta-up">new</span>;
  if (roundedB === 0) return <span className="delta delta-down">removed</span>;
  const pct = (diff / Math.abs(a)) * 100;
  const cls = diff > 0 ? "delta-up" : diff < 0 ? "delta-down" : "delta-flat";
  const sign = diff > 0 ? "+" : diff < 0 ? "" : "±";
  return (
    <span className={`delta ${cls}`}>
      {sign}
      {formatFn(diff)} ({sign}
      {Math.abs(pct).toFixed(0)}%)
    </span>
  );
}

// Merges two { <mergeKey>: string, ... }[] lists into one row per unique
// key, keeping the A-side and B-side values (0 if a key is only on one
// side), sorted by combined size descending so the biggest movers/spenders
// surface first regardless of which side they're bigger on.
function mergeBySide(listA, listB, mergeKey, sideFields) {
  const map = new Map();
  for (const item of listA || []) {
    const k = item[mergeKey];
    map.set(k, { [mergeKey]: k, ...sideFields.a(item), ...sideFields.bDefaults });
  }
  for (const item of listB || []) {
    const k = item[mergeKey];
    const existing = map.get(k);
    if (existing) Object.assign(existing, sideFields.b(item));
    else map.set(k, { [mergeKey]: k, ...sideFields.aDefaults, ...sideFields.b(item) });
  }
  return Array.from(map.values()).sort((x, y) => y.amountA + y.amountB - (x.amountA + x.amountB));
}

// Merge key can no longer be plain `usage_type`: since usage types are now
// also broken down by the Name tag (see ServiceRow), the SAME usage_type can
// legitimately appear as several rows — one per resource. Each list is
// pre-tagged with a composite `_key` (usage_type + name_tag) before merging,
// so two different resources sharing a usage type stay on separate rows
// instead of being silently collapsed into one.
function mergeUsageTypes(listA, listB) {
  const withKey = (list) => (list || []).map((u) => ({ ...u, _key: `${u.usage_type}|${u.name_tag ?? ""}` }));
  return mergeBySide(withKey(listA), withKey(listB), "_key", {
    a: (u) => ({ usage_type: u.usage_type, name_tag: u.name_tag, unit: u.unit, quantityA: u.quantity, amountA: u.amount }),
    b: (u) => ({ usage_type: u.usage_type, name_tag: u.name_tag, unit: u.unit, quantityB: u.quantity, amountB: u.amount }),
    aDefaults: { quantityA: 0, amountA: 0 },
    bDefaults: { quantityB: 0, amountB: 0 },
  });
}

function mergeServices(listA, listB) {
  return mergeBySide(listA, listB, "service", {
    a: (s) => ({ amountA: s.amount }),
    b: (s) => ({ amountB: s.amount }),
    aDefaults: { amountA: 0 },
    bDefaults: { amountB: 0 },
  });
}

function CacheFooter({ cacheInfo, onRefresh }) {
  if (!cacheInfo) return null;
  return (
    <div className="meta-line">
      {cacheInfo.cached ? "Cached" : "Fetched live"} as of {cacheInfo.cachedAt ? new Date(cacheInfo.cachedAt).toLocaleString() : "—"} —{" "}
      <button
        type="button"
        className="link"
        onClick={(e) => {
          e.stopPropagation();
          onRefresh();
        }}
      >
        refresh from AWS
      </button>
    </div>
  );
}

// Renders a usage-type row's Name-tag identifier, or a muted "Untagged"
// placeholder when Cost Explorer returned no value for it (either the
// resource genuinely has no Name tag, or its usage predates the tag being
// activated as a Cost Allocation Tag).
function ResourceCell({ nameTag }) {
  if (!nameTag) return <span className="muted">Untagged</span>;
  return <span className="mono">{nameTag}</span>;
}

// One collapsible service row, plus its lazy-loaded, cached usage-type sub-rows.
// `entry` is either { service, amount } (single-range mode) or
// { service, amountA, amountB } (compare mode).
function ServiceRow({ accountId, range, compareRange, entry, currency, tagFilter }) {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [expanded, setExpanded] = useState(false);
  const [usageTypesA, setUsageTypesA] = useState(null); // null = not yet loaded
  const [usageTypesB, setUsageTypesB] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null); // { cached, cached_at } — range A only, shown as a representative timestamp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const comparing = !!compareRange;

  const fetchUsageTypes = (force) => {
    setLoading(true);
    setError(null);
    start();
    const calls = [api.getCostUsageTypes(idToken, accountId, entry.service, range, force, tagFilter)];
    if (comparing) calls.push(api.getCostUsageTypes(idToken, accountId, entry.service, compareRange, force, tagFilter));
    Promise.all(calls)
      .then(([a, b]) => {
        setUsageTypesA(a.usage_types);
        setUsageTypesB(b ? b.usage_types : null);
        setCacheInfo({ cached: a.cached, cachedAt: a.cached_at });
      })
      .catch((err) => setError(err.message))
      .finally(() => {
        setLoading(false);
        stop();
      });
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && usageTypesA === null && !loading) {
      fetchUsageTypes(false);
    }
  };

  const merged = comparing && usageTypesA && usageTypesB ? mergeUsageTypes(usageTypesA, usageTypesB) : null;

  return (
    <>
      <tr onClick={toggle} className="clickable-row">
        <td>
          <span style={{ display: "inline-block", width: 14, color: "var(--ink-muted)" }}>{expanded ? "▾" : "▸"}</span>
          {entry.service}
        </td>
        {comparing ? (
          <>
            <td>{formatAmount(entry.amountA)}</td>
            <td>{formatAmount(entry.amountB)}</td>
            <td>
              <DeltaCell a={entry.amountA} b={entry.amountB} formatFn={formatAmount} />
            </td>
          </>
        ) : (
          <td>{formatAmount(entry.amount)}</td>
        )}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={comparing ? 4 : 2} className="subtable-wrap">
            {loading && (
              <div className="meta-line" style={{ padding: "10px 16px 10px 38px" }}>
                Loading usage types…
              </div>
            )}
            {error && (
              <div className="error-text" style={{ padding: "10px 16px 10px 38px", fontSize: 13 }}>
                {error}
              </div>
            )}
            {!comparing && usageTypesA && usageTypesA.length === 0 && (
              <div className="meta-line" style={{ padding: "10px 16px 10px 38px" }}>
                No usage-type detail available for this service/date range.
              </div>
            )}
            {comparing && merged && merged.length === 0 && (
              <div className="meta-line" style={{ padding: "10px 16px 10px 38px" }}>
                No usage-type detail available for this service in either range.
              </div>
            )}
            {!comparing && usageTypesA && usageTypesA.length > 0 && (
              <table className="subtable">
                <thead>
                  <tr>
                    <th>Usage type</th>
                    <th>Resource</th>
                    <th>Quantity</th>
                    <th>Amount ({currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {usageTypesA.map((u) => (
                    <tr key={`${u.usage_type}|${u.name_tag ?? ""}`}>
                      <td className="mono">{u.usage_type}</td>
                      <td>
                        <ResourceCell nameTag={u.name_tag} />
                      </td>
                      <td>
                        {formatQuantity(u.quantity)} {u.unit}
                      </td>
                      <td>{formatAmount(u.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {comparing && merged && merged.length > 0 && (
              <table className="subtable">
                <thead>
                  <tr>
                    <th>Usage type</th>
                    <th>Resource</th>
                    <th>Qty A</th>
                    <th>Qty B</th>
                    <th>Amount A</th>
                    <th>Amount B</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {merged.map((u) => (
                    <tr key={u._key}>
                      <td className="mono">{u.usage_type}</td>
                      <td>
                        <ResourceCell nameTag={u.name_tag} />
                      </td>
                      <td>
                        {formatQuantity(u.quantityA)} {u.unit}
                      </td>
                      <td>
                        {formatQuantity(u.quantityB)} {u.unit}
                      </td>
                      <td>{formatAmount(u.amountA)}</td>
                      <td>{formatAmount(u.amountB)}</td>
                      <td>
                        <DeltaCell a={u.amountA} b={u.amountB} formatFn={formatAmount} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(usageTypesA || merged) && <CacheFooter cacheInfo={cacheInfo} onRefresh={() => fetchUsageTypes(true)} />}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ByService() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [range, setRange] = useState(useDefaultRange());
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareRange, setCompareRange] = useState(() => previousPeriod(range));
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [servicesA, setServicesA] = useState([]);
  const [servicesB, setServicesB] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [errorMsg, setErrorMsg] = useState(null);
  const [metaA, setMetaA] = useState(null);
  const [metaB, setMetaB] = useState(null);
  const [tagKeys, setTagKeys] = useState([]);
  const [tagKey, setTagKey] = useState("");
  const [tagValues, setTagValues] = useState([]);
  const [tagValue, setTagValue] = useState("");

  useEffect(() => {
    start();
    api
      .getAccounts(idToken)
      .then((r) => {
        setAccountOptions(r.accounts);
        if (r.accounts.length && !accountId) setAccountId(r.accounts[0].account_id);
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(stop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  // Tag-filter step 1: list available tag keys for this account/range.
  // Resets any in-progress key/value selection when the account or range
  // changes, since a key that had values in one account/range may not exist
  // in another.
  useEffect(() => {
    if (!accountId) return;
    setTagKey("");
    setTagValue("");
    setTagValues([]);
    start();
    api
      .getCostTags(idToken, accountId, range)
      .then((r) => setTagKeys(r.keys || []))
      .catch(() => setTagKeys([]))
      .finally(stop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, accountId, range.startDate, range.endDate]);

  // Tag-filter step 2: once a key is picked, list its available values.
  useEffect(() => {
    setTagValue("");
    if (!accountId || !tagKey) {
      setTagValues([]);
      return;
    }
    start();
    api
      .getCostTags(idToken, accountId, range, tagKey)
      .then((r) => setTagValues(r.values || []))
      .catch(() => setTagValues([]))
      .finally(stop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, accountId, tagKey]);

  const tagFilter = tagKey && tagValue ? { key: tagKey, values: [tagValue] } : null;

  const toggleCompare = () => {
    const next = !compareEnabled;
    setCompareEnabled(next);
    if (next) setCompareRange(previousPeriod(range));
  };

  const loadServices = (force) => {
    if (!accountId) return;
    setErrorMsg(null);
    start();
    const calls = [api.getCostByService(idToken, accountId, range, force, tagFilter)];
    if (compareEnabled) calls.push(api.getCostByService(idToken, accountId, compareRange, force, tagFilter));
    Promise.all(calls)
      .then(([a, b]) => {
        setServicesA(a.services);
        setCurrency(a.currency || "USD");
        setMetaA("cached" in a ? { cached: a.cached, cachedAt: a.cached_at } : null);
        if (b) {
          setServicesB(b.services);
          setMetaB("cached" in b ? { cached: b.cached, cachedAt: b.cached_at } : null);
        } else {
          setServicesB([]);
          setMetaB(null);
        }
      })
      .catch((err) => {
        if (err.message.includes("404")) {
          setServicesA([]);
          setServicesB([]);
          setMetaA(null);
          setMetaB(null);
        } else {
          setErrorMsg(err.message);
        }
      })
      .finally(stop);
  };

  useEffect(() => {
    loadServices(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    idToken,
    accountId,
    range.startDate,
    range.endDate,
    compareEnabled,
    compareRange.startDate,
    compareRange.endDate,
    tagKey,
    tagValue,
  ]);

  const rows = compareEnabled ? mergeServices(servicesA, servicesB) : servicesA.map((s) => ({ service: s.service, amount: s.amount }));

  return (
    <div>
      <div className="topbar">
        <h2>By Service</h2>
        <div className="topbar-controls">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accountOptions.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_name}
              </option>
            ))}
          </select>
          <DateRangePicker value={range} onChange={setRange} />
          <select value={tagKey} onChange={(e) => setTagKey(e.target.value)}>
            <option value="">No tag filter</option>
            {tagKeys.map((k) => (
              <option key={k} value={k}>
                Tag: {k}
              </option>
            ))}
          </select>
          {tagKey && (
            <select value={tagValue} onChange={(e) => setTagValue(e.target.value)}>
              <option value="">Any {tagKey} value</option>
              {tagValues.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          )}
          <label className="checkbox">
            <input type="checkbox" checked={compareEnabled} onChange={toggleCompare} />
            Compare
          </label>
          {compareEnabled && <DateRangePicker value={compareRange} onChange={setCompareRange} />}
        </div>
      </div>
      {errorMsg && <div className="error-banner">{errorMsg}</div>}
      <p className="page-hint">
        {compareEnabled ? (
          <>
            Comparing <strong>A: {rangeLabel(range)}</strong> to <strong>B: {rangeLabel(compareRange)}</strong>. Click a service to
            compare its usage-type breakdown between the two ranges too.
          </>
        ) : null}
        {(metaA || metaB) && (
          <>
            {" "}
            {metaA && (
              <>
                {compareEnabled ? "A" : "List"}: {metaA.cached ? "cached" : "fetched live"} as of{" "}
                {metaA.cachedAt ? new Date(metaA.cachedAt).toLocaleString() : "—"}
              </>
            )}
            {metaB && (
              <>
                {" · B: "}
                {metaB.cached ? "cached" : "fetched live"} as of {metaB.cachedAt ? new Date(metaB.cachedAt).toLocaleString() : "—"}
              </>
            )}{" "}
            —{" "}
            <button type="button" className="link" onClick={() => loadServices(true)}>
              refresh from AWS
            </button>
          </>
        )}
      </p>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Service</th>
              {compareEnabled ? (
                <>
                  <th>A ({currency})</th>
                  <th>B ({currency})</th>
                  <th>Change</th>
                </>
              ) : (
                <th>Amount</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={compareEnabled ? 4 : 2} className="empty-row">
                  No data for this account/date range yet.
                </td>
              </tr>
            )}
            {rows.map((entry) => (
              // Key includes accountId+ranges so switching any of them fully remounts each
              // row instead of reusing a stale, already-expanded instance whose cached
              // usage-type data belongs to a previous account/range combination.
              <ServiceRow
                key={`${accountId}-${range.startDate}-${range.endDate}-${compareEnabled ? `${compareRange.startDate}-${compareRange.endDate}` : "solo"}-${tagKey}-${tagValue}-${entry.service}`}
                accountId={accountId}
                range={range}
                compareRange={compareEnabled ? compareRange : null}
                entry={entry}
                currency={currency}
                tagFilter={tagFilter}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
